import PropTypes from 'prop-types';

import {
    Box,
    Button,
    Heading,
    IconSummaryPanel,
    Link,
    IconSummaryPanelList,
    PayIcon,
    NewTabIcon,
    MeterIcon,
    Text as PText,
    PageRow,
    Paragraph,
    PoundPlusMinusIcon,
    ThumbsUpIcon
} from '@cof/plastic-components';
import { css, styled } from 'styled-components';
import { withText } from '../../../../text/textStore';
import MoneyHelperLogo from '../../../MoneyHelperBanner/MoneyHelperLogo';

const logoStyles = css`
    height: 6.5rem;
    width: 12.9rem;
    margin: 0 auto;

    ${({ theme }: any) => theme.breakpoints.mq.tabletMin} {
        height: 6.7rem;
        width: 13.5rem;
    }
`;

const MoneyHelperLogoStyled = styled(MoneyHelperLogo)`
    ${logoStyles}
`;

const iconStyles = css`
    height: 4rem;
    width: 4rem;
    max-width: 95%;

    ${({ theme }: any) => theme.breakpoints.mq.tabletMin} {
        height: 6rem;
        width: 6rem;
    }
`;

const PoundPlusMinusIconStyled = styled(PoundPlusMinusIcon)`
    ${iconStyles}
`;

const ThumbsUpIconStyled = styled(ThumbsUpIcon)`
    ${iconStyles}
`;

const MeterIconStyled = styled(MeterIcon)`
    ${iconStyles}
`;

const PayIconStyled = styled(PayIcon)`
    ${iconStyles}
`;

const WhatCanYouDoWrapper = styled(Box)`
    ${({ theme }: any) => theme.components.WhatCanYouDoWrapper}
`;

const BackgroundColor = styled.div`
    ${({ theme }: any) => css`
        ${theme.components.HeroWrapper}
    `}
`;

const CustomPageRow = styled(PageRow)`
    ${({ theme }: any) => css`
        padding: ${theme.space.lg} ${theme.space.sm};
        display: flex;
        flex-direction: column;
        align-items: center;

        ${theme.breakpoints.mq.tabletMin} {
            padding: ${theme.space.xl} 0;
            width: 95%;
        }
    `}
`;

const CenteredParagraph = styled.div`
    ${({ theme }: any) => css`
        width: 100%;
        margin: 0 auto;
        text-align: center;
        font-size: large;
        padding-bottom: ${theme.space.lg};

        ${theme.breakpoints.mq.tabletMin} {
            width: 80%;
            font-size: x-large;
        }
    `}
`;

const ColourHeading = styled(Heading)`
    ${({ theme }: any) => css`
        ${theme.components.ScoreDeclineTextColour}
    `}
`;

const StyledHeading = styled.div`
    ${({ theme }: any) => css`
        width: 100%;
        margin: 0 auto;
        padding: 0 0 ${theme.space.md} 0;
        ${theme.components.ScoreDeclineTextColour}

        ${theme.breakpoints.mq.tabletMin} {
            padding: 0 0 ${theme.space.lg} 0;
        }
    `}
`;

const StyledIconSummaryPanelList = styled(IconSummaryPanelList)`
    ${({ theme }: any) => css`
        padding: 0;
        display: flex;
        flex-direction: column;
        align-items: center;

        ${theme.breakpoints.mq.tabletMin} {
            flex-direction: row;
            flex-wrap: wrap;
        }

        ${theme.breakpoints.mq.desktopMin} {
            flex-direction: row;
            flex-wrap: wrap;
        }

        div {
            max-width: none;
        }
    `}
`;

const StyledWhatWeLookAtPanel = styled(IconSummaryPanel)`
    ${({ theme }: any) => css`
        ${theme.components.HeroWrapper}
        align-items: center;
        display: flex;
        margin-right: 0;
        margin-bottom: ${theme.space.xs};
        padding: ${theme.space.sm};

        &:last-child {
            margin-bottom: 0;
        }

        ${theme.breakpoints.mq.tabletMin} {
            padding: ${theme.space.lg};
            margin-right: ${theme.space.sm};
            margin-bottom: 0;

            &:last-child {
                margin-right: 0;
            }
        }
    `}
`;

const StyledWhatCanYouDoNextBox = styled.div`
    ${({ theme }: any) => css`
        padding: ${theme.space.md} ${theme.space.sm};
        text-align: center;
        margin: 0 auto;
        border-radius: 8px;
        background-color: ${theme.colors.global.white};
        margin-bottom: ${theme.space.sm};

        ${theme.breakpoints.mq.tabletMin} {
            width: 65%;
            height: 100%;
            padding: ${theme.space.lg};
        }
    `}
`;

const ButtonContainer = styled.div`
    ${({ theme }: any) => css`
        display: flex;
        flex-direction: column;
        gap: ${theme.space.sm};
        margin: 0;

        ${theme.breakpoints.mq.tabletMin} {
            flex-direction: row;
            justify-content: center;
        }
    `}
`;

const StyledLink = styled(Link)`
    ${({ theme }: any) => css`
        font-size: large;

        ${theme.breakpoints.mq.tabletMin} {
            font-size: x-large;
        }
    `}
`;

// eslint-disable-next-line complexity
const GetToKnow = ({ Text, Text: text, JsxText }: any) => {
    return (
        <>
            <CustomPageRow wrapperBg={'global.white'} maxWidth="110rem">
                <Box data-qa-id="how-did-we-decide-this">
                    <ColourHeading level="2" data-qa-id="heading" pb={['xs', null, 'sm']}>
                        <Text path="HowDidWeDecideThis.Heading" />
                    </ColourHeading>
                    <CenteredParagraph as={Paragraph}>
                        <JsxText
                            path="HowDidWeDecideThis.Body.Copy"
                            params={{
                                EQUIFAX: (
                                    <StyledLink
                                        external
                                        href={text({ path: 'HowDidWeDecideThis.Body.Equifax.Target' })}
                                    >
                                        <Text path="HowDidWeDecideThis.Body.Equifax.Text" />
                                    </StyledLink>
                                ),
                                EXPERIAN: (
                                    <StyledLink
                                        external
                                        href={text({ path: 'HowDidWeDecideThis.Body.Experian.Target' })}
                                    >
                                        <Text path="HowDidWeDecideThis.Body.Experian.Text" />
                                    </StyledLink>
                                )
                            }}
                        />
                    </CenteredParagraph>
                    <StyledHeading as={Heading} level="3" data-qa-id="heading">
                        <Text path="WhatWeLookAt.Heading" />
                    </StyledHeading>
                    <StyledIconSummaryPanelList colorVariant="dark" mobileLayoutVariant="condensed">
                        <StyledWhatWeLookAtPanel Icon={PoundPlusMinusIconStyled} data-qa-id="panel-1">
                            <PText>{text({ path: 'WhatWeLookAt.Points.Point1.Body' })}</PText>
                        </StyledWhatWeLookAtPanel>
                        <StyledWhatWeLookAtPanel Icon={ThumbsUpIconStyled} data-qa-id="panel-2">
                            <PText>{text({ path: 'WhatWeLookAt.Points.Point2.Body' })}</PText>
                        </StyledWhatWeLookAtPanel>
                        <StyledWhatWeLookAtPanel Icon={MeterIconStyled} data-qa-id="panel-3">
                            <PText>{text({ path: 'WhatWeLookAt.Points.Point3.Body' })}</PText>
                        </StyledWhatWeLookAtPanel>
                        <StyledWhatWeLookAtPanel Icon={PayIconStyled} data-qa-id="panel-4">
                            <PText>{text({ path: 'WhatWeLookAt.Points.Point4.Body' })}</PText>
                        </StyledWhatWeLookAtPanel>
                    </StyledIconSummaryPanelList>
                </Box>
            </CustomPageRow>
            <BackgroundColor>
                <CustomPageRow maxWidth="110rem">
                    <WhatCanYouDoWrapper>
                        <ColourHeading level="2" data-qa-id="heading" pb={['md', null, 'lg']}>
                            <Text path="WhatCanYouDo.Heading" />
                        </ColourHeading>
                    </WhatCanYouDoWrapper>
                    <StyledWhatCanYouDoNextBox data-qa-id="moneyhelper-box">
                        <MoneyHelperLogoStyled />
                        <ColourHeading level="3" pt={'sm'} pb={'xs'} data-qa-id="heading">
                            <Text path="WhatCanYouDo.MoneyHelperPartners.Heading" />
                        </ColourHeading>
                        <Paragraph pb={'md'} data-qa-id="body">
                            <Text path="WhatCanYouDo.MoneyHelperPartners.Body" />
                        </Paragraph>
                        <ButtonContainer>
                            <Button
                                as="a"
                                href={text({ path: 'WhatCanYouDo.MoneyHelperPartners.Button1.GuideLink' })}
                                data-qa-id="check-creditreport-button"
                                // @ts-expect-error TS2322: Type ' children: Element[]; as: "a"; href: any; "data-qa-id": string; target: string; rel: string; '
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Text path="WhatCanYouDo.MoneyHelperPartners.Button1.Text" />
                                <NewTabIcon
                                    // @ts-expect-error TS2322: Type ' role: string; verticalAlign: string; ml: "0.5rem"; pb: "0.3rem"; "aria-label": string; ' is n
                                    role="img"
                                    verticalAlign="middle"
                                    ml="0.5rem"
                                    pb={'0.3rem'}
                                    aria-label=". This link opens in a new tab"
                                />
                            </Button>
                            <Button
                                as="a"
                                href={text({ path: 'WhatCanYouDo.MoneyHelperPartners.Button2.GuideLink' })}
                                data-qa-id="get-moneyhelper-button"
                                variant="secondary"
                                // @ts-expect-error TS2322: Type ' children: Element[]; as: "a"; href: any; "data-qa-id": string; variant: "secondary"; target:
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Text path="WhatCanYouDo.MoneyHelperPartners.Button2.Text" />
                                <NewTabIcon
                                    // @ts-expect-error TS2322: Type ' role: string; verticalAlign: string; ml: "0.5rem"; pb: "0.3rem"; "aria-label": string; ' is n
                                    role="img"
                                    verticalAlign="middle"
                                    ml="0.5rem"
                                    pb={'0.3rem'}
                                    aria-label=". This link opens in a new tab"
                                />
                            </Button>
                        </ButtonContainer>
                    </StyledWhatCanYouDoNextBox>
                </CustomPageRow>
            </BackgroundColor>
        </>
    );
};

GetToKnow.propTypes = {
    Text: PropTypes.func.isRequired,
    JsxText: PropTypes.func.isRequired
};

const WrappedGetToKnowSection = withText('ScoreDecline')(GetToKnow);

WrappedGetToKnowSection.displayName = 'GetToKnowSection';

export default WrappedGetToKnowSection;
